import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { errMsg, ghostBtn, inputStyle, primaryBtn } from "../tasks/common";
import type { KSubject } from "../tasks/v2/common";

const TIPOS: [string, string][] = [["ajuste", "Ajuste"], ["acao", "Ação"], ["projeto", "Projeto"]];

// Painel Dev › Kanban: assuntos por tipo de task do Quadro v2 (criar / renomear / desativar).
// Só Admin ou kanban_total escreve (backend barra). Inativo some da criação, fica nos cards antigos.
export function KanbanTab() {
  const qc = useQueryClient();
  const { data: all = [] } = useQuery<KSubject[]>({ queryKey: ["kanban", "subjects-all"], queryFn: () => api.get("/kanban/subjects/all") });
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const done = () => { setErr(null); qc.invalidateQueries({ queryKey: ["kanban"] }); };
  const fail = (e: unknown) => setErr(errMsg(e));

  const create = useMutation({
    mutationFn: (b: { tipo: string; name: string }) => api.post("/kanban/subjects", b),
    onSuccess: (_d, b) => { setDraft(d => ({ ...d, [b.tipo]: "" })); done(); },
    onError: fail,
  });
  const patch = useMutation({
    mutationFn: ({ id, ...b }: { id: string; name?: string; active?: boolean }) => api.patch(`/kanban/subjects/${id}`, b),
    onSuccess: () => { setEditing(null); done(); },
    onError: fail,
  });

  return (
    <div>
      <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)", marginBottom: 4 }}>Kanban (Quadro v2) · Assuntos por tipo de task</div>
      <div style={{ fontSize: 12, color: "var(--ink-muted)", marginBottom: 16 }}>Toda task do Quadro v2 escolhe um assunto do seu tipo. Desativar tira da lista de criação sem mexer nos cards que já usam.</div>
      {err && <div style={{ fontSize: 12, color: "var(--danger)", marginBottom: 12 }}>{err}</div>}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
        {TIPOS.map(([tipo, label]) => {
          const items = all.filter(s => s.tipo === tipo);
          const value = draft[tipo] ?? "";
          return (
            <div key={tipo} style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 10, padding: 14, display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>{label} <span style={{ color: "var(--ink-muted)", fontWeight: 400 }}>· {items.filter(s => s.active).length} ativos</span></div>
              {items.map(s => (
                <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: s.active ? "var(--ink)" : "var(--ink-muted)" }}>
                  {editing?.id === s.id ? (
                    <>
                      <input autoFocus value={editing.name} maxLength={80} onChange={e => setEditing({ id: s.id, name: e.target.value })} onKeyDown={e => { if (e.key === "Enter" && editing.name.trim()) patch.mutate({ id: s.id, name: editing.name }); if (e.key === "Escape") setEditing(null); }} style={{ ...inputStyle, padding: "4px 8px" }} />
                      <button onClick={() => patch.mutate({ id: s.id, name: editing.name })} disabled={!editing.name.trim()} style={{ ...primaryBtn, padding: "4px 10px", fontSize: 12 }}>Salvar</button>
                      <button onClick={() => setEditing(null)} style={{ ...ghostBtn, padding: "4px 10px", fontSize: 12 }}>×</button>
                    </>
                  ) : (
                    <>
                      <span style={{ flex: 1, textDecoration: s.active ? "none" : "line-through" }}>{s.name}</span>
                      <button onClick={() => setEditing({ id: s.id, name: s.name })} style={{ background: "none", border: "none", color: "var(--ink-muted)", fontSize: 12, cursor: "pointer" }}>Renomear</button>
                      <button onClick={() => patch.mutate({ id: s.id, active: !s.active })} style={{ background: "none", border: "none", color: s.active ? "var(--danger)" : "var(--ok)", fontSize: 12, cursor: "pointer" }}>{s.active ? "Desativar" : "Reativar"}</button>
                    </>
                  )}
                </div>
              ))}
              {!items.length && <div style={{ fontSize: 12, color: "var(--ink-dim)" }}>Nenhum assunto ainda.</div>}
              <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
                <input value={value} maxLength={80} placeholder={tipo === "acao" ? "Ex.: Chamar cliente, Reunião…" : "Novo assunto"} onChange={e => setDraft(d => ({ ...d, [tipo]: e.target.value }))} onKeyDown={e => { if (e.key === "Enter" && value.trim()) create.mutate({ tipo, name: value }); }} style={{ ...inputStyle, padding: "6px 8px" }} />
                <button onClick={() => create.mutate({ tipo, name: value })} disabled={!value.trim() || create.isPending} style={{ ...primaryBtn, padding: "6px 12px", fontSize: 12, whiteSpace: "nowrap" }}>+ Criar</button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
